import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { UserCheck, Sparkles, Briefcase, Send } from "lucide-react";

const ICONS = {
  apply: Send,
  contact: UserCheck,
  signup: Sparkles,
  job: Briefcase,
};

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ActivityTicker() {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get("/stats/activity")
        .then((r) => {
          if (alive) setEvents(r.data.events || []);
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (events.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % events.length), 5000);
    return () => clearInterval(t);
  }, [events.length]);

  if (!events.length) return null;
  const ev = events[idx];
  const Icon = ICONS[ev.type] || Sparkles;

  return (
    <div
      data-testid="activity-ticker"
      className="border-y border-neutral-800 bg-neutral-950 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
          </span>
          <span className="text-[10px] uppercase tracking-widest text-amber-500 font-bold hidden sm:inline">Live</span>
        </div>
        <div
          key={idx}
          data-testid="ticker-event"
          className="flex items-center gap-3 flex-1 min-w-0 animate-in fade-in slide-in-from-right-4 duration-500"
        >
          <Icon className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="text-sm text-neutral-200 truncate">{ev.text}</span>
          <span className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0 hidden md:inline">
            {timeAgo(ev.at)}
          </span>
        </div>
        {events.length > 1 && (
          <div className="hidden md:flex items-center gap-1 shrink-0">
            {events.slice(0, Math.min(events.length, 8)).map((_, i) => (
              <span
                key={i}
                className={`h-1 transition-all ${i === idx ? "w-6 bg-amber-500" : "w-1 bg-neutral-700"}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
